import "./application-details-modal.scss";
import { CrossIcon } from "../../../../../assets/svg";
import { Helpers } from "../../../../../utils/helper";
import { Separator } from "../../../../separator";
import { OwnerTable } from "../owner-table/owner-table";
import ProposedHouseholdTable from "../proposed-household-table/proposed-household-table";
import { forwardRef, useImperativeHandle, useRef, useState, MouseEvent } from "react";
import AccordionListing from "../accordion-listing/accordion-listing";
import { TextInfo } from "../../../../text";
import EligibilityDetails from "../eligibility-details/eligibility-details";
import { useAppSelector } from "../../../../../redux/hook";
import Declaration from "../../../../../pages/application-coo/review-endorse/declaration";
import FinancialDetails from "../financial-details/financial-details";
import ConveyancingDetails from "../conveyancing-details/conveyancing-details";
import DocumentDetails from "../document-details/document-details";
import ReviewDeclaration from "../review-declaration/review-declaration";
import { ModalBackground } from "../../../shared/modal-background";

export interface ApplicationDetailsModalHandle {
  getIsShowing: () => boolean;
  setIsShowing: (value: boolean) => void;
}

const ApplicationDetailsModal = forwardRef<ApplicationDetailsModalHandle, {}>((props, ref) => {
  const applicationStartSlice = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const applicationStep1 = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const applicationStep2 = useAppSelector((state) => state.app.ApplicationStep2Slice.formData);
  const applicationStep3 = useAppSelector((state) => state.app.ApplicationStep3Slice.formData);
  const applicationStep4 = useAppSelector((state) => state.app.ApplicationStep4Slice.formData);
  const applicationStartFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);

  const [isShowing, setIsShowing] = useState<boolean>(false);

  const modalContainerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => {
      return {
        getIsShowing: () => isShowing,
        setIsShowing: (value: boolean) => {
          setIsShowing(value);
        },
      };
    },
    [isShowing]
  );

  return isShowing ? (
    <ModalBackground innerComponentRef={modalContainerRef} callback={() => setIsShowing(false)}>
      <div ref={modalContainerRef} className="polymer-application-details-modal__container">
        <div className="polymer-application-details-modal__header-container">
          <div>
            <h2 className="polymer-application-details-modal__title">Application Details</h2>
            <p className="polymer-application-details-modal__submit-date">Submitted on {Helpers.formatDate(applicationStartSlice.submit_date, "long")}</p>
          </div>
          <button onClick={() => setIsShowing(false)}>
            <CrossIcon fillColor="#0C8188" />
          </button>
        </div>

        <div className="polymer-application-details-modal__container-scrollable">
          <h3 className="polymer-application-details-modal__subtitle">Summary</h3>
          <OwnerTable style={{ borderStyle: "solid", borderWidth: 0.5, borderColor: "#D9D9D9", padding: 24, marginBottom: 48 }} />

          <h3 className="polymer-application-details-modal__subtitle">Proposed Household Details</h3>
          <div style={{ marginTop: 24 }}>
            <p className="polymer-application-details-modal__paragraph-bold">Proposed Holding Type</p>
            <ProposedHouseholdTable style={{ borderStyle: "solid", borderWidth: 0.5, borderColor: "#D9D9D9", padding: "0 24px 24px", marginBottom: 48 }} />
          </div>
          <div style={{ marginTop: 24 }}>
            <p className="polymer-application-details-modal__paragraph-bold">Owners</p>
            <AccordionListing type={"owner"} />
          </div>
          <div style={{ marginTop: 24 }}>
            <p className="polymer-application-details-modal__paragraph-bold">Occupiers</p>
            {applicationStep1.household.incoming_occupiers.length > 0 ? <AccordionListing type={"occupier"} /> : <div> - </div>}
          </div>

          <div className="polymer-application-details-modal__declaration">
            <h3 className="polymer-application-details-modal__subtitle">Declaration in Ownership of Private Property</h3>
            <div className="polymer-application-details-modal__declaration-section">
              <TextInfo
                variant="column"
                title={`Do any of the proposed owners own or have an interest in a private property (including properties outside of Singapore)?`}
                description={applicationStep1.household.check_has_private_property ? "Yes" : "No"}
              />
            </div>
          </div>

          <Separator variant="solid" width={1} style={{ margin: "48px 0" }} />

          <h2 className="polymer-application-details-modal__title">Eligibility</h2>
          <EligibilityDetails details={applicationStep1} />

          <Separator variant="solid" width={1} style={{ margin: "48px 0" }} />

          <h2 className="polymer-application-details-modal__title">Financing</h2>
          <FinancialDetails details={applicationStep2} />

          <Separator variant="solid" width={1} style={{ margin: "48px 0" }} />

          <h2 className="polymer-application-details-modal__title">Conveyancing</h2>
          <ConveyancingDetails details={applicationStep3} />

          <Separator variant="solid" width={1} style={{ margin: "48px 0" }} />

          <h2 className="polymer-application-details-modal__title">Uploaded Documents</h2>
          <DocumentDetails details={applicationStep4} />
          <Separator variant="solid" width={1} style={{ marginBottom: 48 }} />

          <h2 className="polymer-application-details-modal__title">Declaration and Authorisation</h2>
          <Declaration checkboxValue setCheckboxValue={() => {}} showTitle={false} hasPadding={false} isDisabled />
          <ReviewDeclaration mainApplicantUinfin={applicationStartFormData.selected_hdb_property?.main_applicant_uinfin || ""} details={applicationStep1} />
        </div>
      </div>
    </ModalBackground>
  ) : null;
});

export { ApplicationDetailsModal };
